import React, { useEffect, useState } from "react";
import { useForm, useSelect } from "@refinedev/antd";
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Row,
  Col,
  Typography,
  Tooltip,
  Badge,
  message,
  Space,
  Switch,
  DatePicker,
  Alert
} from "antd";
import type { FormListFieldData, FormListOperation } from "antd/es/form/FormList";
import { MinusCircleOutlined, PlusOutlined, InfoCircleOutlined, FileTextOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../api";
import { PageHeader } from "../../components/ui";

const { Text } = Typography;

interface QuotationItem {
  quotationId: number;
  inventoryItemId?: number;
  itemCode: string;
  itemName: string;
  itemClassCode: string;
  quantity: number;
  taxId?: number;
  taxRate: number;
  unitPrice: number;
  discountRate: number;
  discountAmount: number;
  totalAmount: number;
  taxAmount: number;
  lineAmount: number;
  isActive: boolean;
  priceId?: number;
  taxType?: string;
}

interface BillableService {
  description: string;
  price: number;
  taxId?: number;
  taxRate?: number;
  taxType?: string;
}

interface TaxOption {
  label: string;
  value: number;
  data: {
    id: number;
    taxType: string;
    rate: number;
    isActive: boolean;
  };
}

export const QuotationCreate: React.FC = () => {
  const [isCashQuotation, setIsCashQuotation] = useState(false);
  const [showBillableServices, setShowBillableServices] = useState(false);
  const [isCustomerCollapsed, setIsCustomerCollapsed] = useState(false);
  const [isManualCustomerEntry, setIsManualCustomerEntry] = useState(false);
  const [standardTaxRate, setStandardTaxRate] = useState(15.5);
  const [taxOptions, setTaxOptions] = useState<TaxOption[]>([]);

  const { form, formProps, saveButtonProps } = useForm({
    resource: "quotations",
    redirect: "list",
    meta: {
      fields: [
        "quotationNumber",
        "customerTPIN",
        "customerName",
        "currencyTypeCode",
        "exchangeRate",
        "subTotal",
        "totalTaxableAmount",
        "totalTax",
        "totalAmount",
        "status",
        "quotationDate",
        "validUntil",
        "notes",
        "terms",
        {
          items: [
            "quotationId",
            "inventoryItemId",
            "itemCode",
            "itemName",
            "itemClassCode",
            "quantity",
            "taxId",
            "taxRate",
            "unitPrice",
            "discountRate",
            "discountAmount",
            "totalAmount",
            "taxAmount",
            "lineAmount",
            "isActive",
            "taxType"
          ]
        },
        {
          billableServices: [
            "description",
            "price",
            "taxId",
            "taxRate",
            "taxType"
          ]
        }
      ]
    }
  });

  const { selectProps: customerSelectProps, queryResult: customerQueryResult } = useSelect({
    resource: "customers",
    optionLabel: "name",
    optionValue: "tpin",
    meta: {
      fields: [
        "id",
        "name",
        "tpin",
        "code",
        "customerNumber",
        "description",
        "isActive",
        {
          customerContacts: ["id", "type", "value", "isPrimary"]
        },
        {
          customerAddresses: ["id", "type", "street", "city", "state", "country", "postalCode", "isPrimary"]
        }
      ]
    },
    onSearch: (value: string) => [{ field: "name", operator: "contains", value }],
  });

  const { selectProps: itemSelectProps, queryResult: itemQueryResult } = useSelect({
    resource: "inventory/available",
    optionLabel: "itemName",
    optionValue: "itemCode",
    meta: {
      fields: [
        "id", "itemCode", "itemTypeCode", "itemClassificationCode", "itemName", "stockLevelId", "taxId", "barcode", "additionalInformation", "isActive",
        { stockLevel: ["id", "minimumQuantity", "maximumQuantity", "reorderPoint", "lastUpdated", "itemCode", "currentStockLevel", "reservedStock", "availableStock"] },
        { tax: ["id", "taxType", "taxTypeId", "rate", "isActive"] },
        { prices: ["id", "itemCode", "inventoryItemId", "basePrice", "discountType", "discountValue", "minimumQuantity", "maximumQuantity", "effectivePrice", "isActive", "currencyCode", "startDate", "endDate"] }
      ]
    },
    onSearch: (value: string) => [
      { field: "itemCode", operator: "contains", value },
      { field: "itemName", operator: "contains", value }
    ]
  });

  const { selectProps: currencySelectProps } = useSelect({
    resource: "currency",
    optionLabel: "currencyCode",
    optionValue: "currencyCode",
    meta: { fields: ["id", "currencyCode", "currencyName", "exchangeRate", "isBaseCurrency", "isActive"] }
  });

  const { isLoading: taxesLoading } = useQuery({
    queryKey: ['taxes'],
    queryFn: () => api.getTaxes(),
    onSuccess: (data) => {
      const options = data.map((tax: any) => ({
        label: `${tax.taxType} (${tax.rate}%)`,
        value: tax.id,
        data: tax
      }));
      setTaxOptions(options);
      if (options.length > 0) {
        setStandardTaxRate(options[0].data.rate);
      }
    },
    onError: (error) => {
      console.error("Error fetching taxes:", error);
    }
  });

  const handleItemSelect = async (value: string, name: number) => {
    const itemData = itemQueryResult?.data?.data?.find((item: any) => item.itemCode === value);
    if (itemData) {
      const currentItems = form.getFieldValue('items') || [];
      const activePrices = itemData.prices?.filter((price: any) => price.isActive) || [];

      if (activePrices.length === 0) {
        message.warning(`No active prices found for ${itemData.itemName}`);
        return;
      }

      const defaultPrice = activePrices.find((price: any) => price.isDefault) || activePrices[0];
      let discountRate = 0;
      if (defaultPrice?.discountType === "Percentage") {
        discountRate = defaultPrice.discountValue / 100;
      } else if (defaultPrice?.discountType === "FixedAmount" && defaultPrice.basePrice > 0) {
        discountRate = defaultPrice.discountValue / defaultPrice.basePrice;
      }

      const updatedItems = [...currentItems];
      updatedItems[name] = {
        ...updatedItems[name],
        inventoryItemId: itemData.id,
        itemCode: itemData.itemCode,
        itemName: itemData.itemName,
        itemClassCode: itemData.itemClassificationCode,
        unitPrice: defaultPrice?.effectivePrice || 0,
        taxId: itemData.tax?.id || undefined,
        taxRate: itemData.tax?.rate || 0,
        taxType: itemData.tax?.taxType || "VAT",
        discountRate: discountRate,
        priceId: defaultPrice?.id || null,
        quantity: 1,
        isActive: true
      };

      form.setFieldsValue({ items: updatedItems });
      updateLineAmounts(name);

      if (activePrices.length > 1) {
        message.info(`${itemData.itemName}: Auto-selected default price. ${activePrices.length} pricing options available.`);
      } else if (defaultPrice) {
        message.success(`${itemData.itemName}: Price set to ${defaultPrice.currencyCode} ${defaultPrice.effectivePrice.toFixed(2)}`);
      }
    }
  };

  const handlePriceSelect = (priceId: number, name: number) => {
    const currentItems = form.getFieldValue('items') || [];
    const currentItem = currentItems[name];
    if (!currentItem || !currentItem.inventoryItemId) {
      message.warning('Please select an item first');
      return;
    }

    const itemData = itemQueryResult?.data?.data?.find((item: any) => item.id === currentItem.inventoryItemId);
    if (!itemData) {
      message.error('Item data not found');
      return;
    }

    const selectedPrice = itemData.prices?.find((price: any) => price.id === priceId);
    if (!selectedPrice) {
      message.error('Selected price not found');
      return;
    }

    updateItemWithPrice(itemData, selectedPrice, name);
  };

  const updateItemWithPrice = (itemData: any, price: any, name: number) => {
    const currentItems = form.getFieldValue('items') || [];
    let discountRate = 0;
    if (price.discountType === "Percentage") {
      discountRate = price.discountValue / 100;
    } else if (price.discountType === "FixedAmount" && price.basePrice > 0) {
      discountRate = price.discountValue / price.basePrice;
    }

    const taxId = itemData.tax?.id || undefined;
    const taxRate = itemData.tax?.rate || 0;
    const taxType = itemData.tax?.taxType || "VAT";

    const updatedItems = [...currentItems];
    updatedItems[name] = {
      ...updatedItems[name],
      inventoryItemId: itemData.id,
      itemCode: itemData.itemCode,
      itemName: itemData.itemName,
      itemClassCode: itemData.itemClassificationCode,
      unitPrice: price.effectivePrice,
      taxId: taxId,
      taxRate: taxRate,
      taxType: taxType,
      discountRate: discountRate,
      priceId: price.id,
      quantity: 1,
      isActive: true
    };

    form.setFieldsValue({
      items: updatedItems,
      currencyTypeCode: price.currencyCode
    });

    updateLineAmounts(name);
  };

  const generateQuotationNumber = (): string => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let quotationNumber = 'QT-';
    for (let i = 0; i < 6; i++) {
      quotationNumber += characters[Math.floor(Math.random() * characters.length)];
    }
    return quotationNumber;
  };

  useEffect(() => {
    const quotationNumber = generateQuotationNumber();
    const today = dayjs();
    const validUntil = today.add(30, 'days');

    form.setFieldsValue({
      quotationNumber,
      status: "Draft",
      currencyTypeCode: "USD",
      exchangeRate: 1,
      quotationDate: today,
      validUntil: validUntil,
      items: [],
      billableServices: []
    });
  }, [form]);

  const disablePastDates = (current: dayjs.Dayjs) => {
    return current && current < dayjs().startOf('day');
  };

  const updateTotalsWithServices = () => {
    const currentItems = form.getFieldValue('items') || [];
    const billableServices = form.getFieldValue('billableServices') || [];

    const itemTotals = currentItems.reduce((acc: any, item: QuotationItem) => {
      const itemTotal = Number(item.totalAmount) || 0;
      const itemTaxAmount = Number(item.taxAmount) || 0;
      const itemTaxableAmount = Number(item.lineAmount) || (itemTotal - itemTaxAmount);
      return {
        totalTaxableAmount: acc.totalTaxableAmount + itemTaxableAmount,
        totalTax: acc.totalTax + itemTaxAmount,
        totalAmount: acc.totalAmount + itemTotal,
      };
    }, { totalTaxableAmount: 0, totalTax: 0, totalAmount: 0 });

    const serviceTotals = billableServices.reduce((acc: any, service: BillableService) => {
      const servicePrice = Number(service.price) || 0;
      const taxRate = (service.taxRate !== undefined ? service.taxRate : standardTaxRate) / 100;
      const taxDivisor = 1 + taxRate;
      const taxableAmount = taxDivisor !== 0 ? servicePrice / taxDivisor : 0;
      const taxAmount = servicePrice - taxableAmount;
      return {
        totalTaxableAmount: acc.totalTaxableAmount + taxableAmount,
        totalTax: acc.totalTax + taxAmount,
        totalAmount: acc.totalAmount + servicePrice,
      };
    }, { totalTaxableAmount: 0, totalTax: 0, totalAmount: 0 });

    const combinedTotals = {
      totalTaxableAmount: itemTotals.totalTaxableAmount + serviceTotals.totalTaxableAmount,
      totalTax: itemTotals.totalTax + serviceTotals.totalTax,
      totalAmount: itemTotals.totalAmount + serviceTotals.totalAmount,
      subTotal: itemTotals.totalTaxableAmount + serviceTotals.totalTaxableAmount
    };

    form.setFieldsValue({
      subTotal: combinedTotals.subTotal,
      totalTaxableAmount: combinedTotals.totalTaxableAmount,
      totalTax: combinedTotals.totalTax,
      totalAmount: combinedTotals.totalAmount,
    });
  };

  const updateLineAmounts = (name: number) => {
    const currentItems = form.getFieldValue('items') || [];
    const currentItem = currentItems[name];
    if (!currentItem) return;

    const quantity = Number(currentItem.quantity) || 0;
    const unitPrice = Number(currentItem.unitPrice) || 0;
    const taxRate = (Number(currentItem.taxRate) || 0) / 100;
    const discountRate = Number(currentItem.discountRate) || 0;

    const lineAmount = quantity * unitPrice;
    const discountAmount = lineAmount * discountRate;
    const totalAmount = lineAmount - discountAmount;
    const taxDivisor = 1 + taxRate;
    const taxableAmount = taxDivisor !== 0 ? totalAmount / taxDivisor : 0;
    const taxAmount = totalAmount - taxableAmount;

    currentItems[name] = { ...currentItem, lineAmount, discountAmount, taxAmount, totalAmount };
    form.setFieldsValue({ items: currentItems });
    updateTotalsWithServices();
  };

  const handleQuantityChange = (name: number) => updateLineAmounts(name);
  const handlePriceChange = (name: number) => updateLineAmounts(name);
  const handleTaxRateChange = (name: number) => updateLineAmounts(name);

  const handleCustomerSelect = (customerId: number) => {
    const selectedCustomer = customerQueryResult?.data?.data?.find((customer: any) => customer.id === customerId);
    if (selectedCustomer) {
      form.setFieldsValue({
        customerId: selectedCustomer.id,
        customerTPIN: selectedCustomer.tpin || "0",
        customerName: selectedCustomer.name
      });
    }
  };

  const handleCashQuotationToggle = (checked: boolean) => {
    setIsCashQuotation(checked);
    if (checked) {
      form.setFieldsValue({ customerId: undefined, customerTPIN: "CASH-QUOTATION", customerName: "Cash Customer" });
    } else {
      form.setFieldsValue({ customerId: undefined, customerTPIN: undefined, customerName: undefined });
    }
  };

  const toggleBillableServices = (checked: boolean) => {
    setShowBillableServices(checked);
    setIsCustomerCollapsed(checked);

    if (!checked) {
      form.setFieldsValue({ billableServices: [] });
    } else {
      const currentBillableServices = form.getFieldValue('billableServices') || [];
      if (currentBillableServices.length === 0) {
        const defaultTaxId = taxOptions.length > 0 ? taxOptions[0].value : undefined;
        const defaultTaxType = taxOptions.length > 0 ? taxOptions[0].data.taxType : undefined;
        const defaultTaxRate = taxOptions.length > 0 ? taxOptions[0].data.rate : standardTaxRate;
        form.setFieldsValue({
          billableServices: [{ description: '', price: 0, taxId: defaultTaxId, taxRate: defaultTaxRate, taxType: defaultTaxType }]
        });
      }
    }
    updateTotalsWithServices();
  };

  const toggleManualCustomerEntry = (checked: boolean) => {
    setIsManualCustomerEntry(checked);
    if (checked) {
      form.setFieldsValue({ customerId: undefined, customerTPIN: '', customerName: '', customerContact: '', customerStreet: '', customerCity: '', customerProvince: '', customerCountry: 'Zimbabwe' });
    } else {
      form.setFieldsValue({ customerId: undefined, customerName: '', customerContact: '', customerTPIN: undefined, customerStreet: '', customerCity: '', customerProvince: '', customerCountry: '' });
    }
  };

  const handleServiceTaxSelect = (taxId: number, index: number) => {
    const selectedTax = taxOptions.find(option => option.value === taxId);
    if (selectedTax) {
      const { taxType, rate } = selectedTax.data;
      const billableServices = form.getFieldValue('billableServices') || [];
      billableServices[index] = { ...billableServices[index], taxId: taxId, taxRate: rate, taxType: taxType };
      form.setFieldsValue({ billableServices });
      updateTotalsWithServices();
    }
  };

  const handleItemTaxSelect = (taxId: number, name: number) => {
    const selectedTax = taxOptions.find(option => option.value === taxId);

    if (selectedTax) {
      const { taxType, rate } = selectedTax.data;

      const currentItems = form.getFieldValue('items') || [];
      currentItems[name] = {
        ...currentItems[name],
        taxId: taxId,
        taxRate: rate,
        taxType: taxType
      };

      form.setFieldsValue({ items: currentItems });
      updateLineAmounts(name);
    }
  };

  return (
    <div className="page-container">
      <PageHeader
        title="Create New Quotation"
        icon={<FileTextOutlined />}
        breadcrumbs={[
          { title: "Quotations", href: "/quotations" },
          { title: "Create" },
        ]}
      />

      <Form
        {...formProps}
        form={form}
        layout="vertical"
        initialValues={{ quotationNumber: generateQuotationNumber(), status: "Draft", exchangeRate: 1 }}
        className="create-form-layout"
      >
        {/* Quick Options */}
        <div className="info-card" style={{ marginBottom: 16 }}>
          <div className="info-card-header">
            <span className="info-card-title">Quick Options</span>
          </div>
          <div className="info-card-body">
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={8}>
                <Space align="center" size={8}>
                  <Switch checked={isCashQuotation} onChange={handleCashQuotationToggle} checkedChildren="Cash" unCheckedChildren="Regular" />
                  <Text type="secondary" style={{ fontSize: '12px' }}>Quick cash quotation</Text>
                </Space>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="quotationDate" rules={[{ required: true, message: 'Date required' }]} style={{ margin: 0 }} label="Quotation Date">
                  <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder="Quotation date" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Space align="center" size={8}>
                  <Switch checked={showBillableServices} onChange={toggleBillableServices} checkedChildren="Services" unCheckedChildren="No Services" />
                  <Text type="secondary" style={{ fontSize: '12px' }}>Add billable services</Text>
                </Space>
              </Col>
            </Row>
          </div>
        </div>

        {/* Customer Information */}
        <div className="info-card" style={{ marginBottom: 16 }}>
          <div className="info-card-header">
            <span className="info-card-title">Customer Information</span>
            <Badge count={form.getFieldValue('status') === "Accepted" ? "ACCEPTED" : "DRAFT"} style={{ backgroundColor: form.getFieldValue('status') === "Accepted" ? "#10b981" : "#94a3b8", color: "#fff", fontSize: '11px' }} />
          </div>
          <div className="info-card-body">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Form.Item label="Quotation No" name="quotationNumber" rules={[{ required: true, message: 'Required' }]}>
                  <Input readOnly style={{ fontWeight: 'bold' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item label="Valid Until" name="validUntil" rules={[{ required: true, message: 'Required' }]}>
                  <DatePicker style={{ width: '100%' }} disabledDate={disablePastDates} format="YYYY-MM-DD" placeholder="Valid until date" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item label=" ">
                  <Space align="center" size={8}>
                    <Switch checked={isManualCustomerEntry} onChange={toggleManualCustomerEntry} checkedChildren="New Customer" unCheckedChildren="Select Existing" disabled={isCashQuotation} />
                    <Tooltip title="Toggle to create a new customer or select existing">
                      <InfoCircleOutlined style={{ color: '#3b82f6', fontSize: '14px' }} />
                    </Tooltip>
                  </Space>
                </Form.Item>
              </Col>

              {!isManualCustomerEntry ? (
                <Col xs={24} sm={24} md={6}>
                  <Form.Item label="Select Customer" name="customerId" help={<Text type="secondary" style={{ fontSize: '11px' }}>Select from existing customers</Text>}>
                    <Select placeholder={isCashQuotation ? "Cash Customer" : "Search customer"} showSearch disabled={isCashQuotation} onChange={handleCustomerSelect}
                      filterOption={(input: string, option: any) => option?.label?.toString().toLowerCase().includes(input.toLowerCase())}
                      options={customerQueryResult?.data?.data?.map((customer: any) => {
                        const isManualEntry = !customer.tpin || customer.tpin === "0";
                        const label = isManualEntry ? `${customer.name} (Manual Entry)` : `${customer.name} (${customer.tpin})`;
                        return { label, value: customer.id, data: customer };
                      }) || []}
                    />
                  </Form.Item>
                  <Form.Item name="customerTPIN" hidden><Input /></Form.Item>
                  <Form.Item name="customerName" hidden><Input /></Form.Item>
                </Col>
              ) : (
                <>
                  <Col span={24}>
                    <Alert message="Create New Customer" description="Enter details for a new customer. This customer will be saved for future transactions." type="info" showIcon style={{ marginBottom: 16 }} banner />
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item label="Customer Name" name="customerName" rules={[{ required: isManualCustomerEntry, message: 'Customer name required' }, { min: 2, message: 'Min 2 characters' }]}>
                      <Input placeholder="Customer full name" disabled={isCashQuotation} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item label="TPIN (Optional)" name="customerTPIN">
                      <Input placeholder="TPIN (optional)" disabled={isCashQuotation} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item label="Contact" name="customerContact" rules={[{ required: isManualCustomerEntry, message: 'Contact required' }]}>
                      <Input placeholder="Phone/Email" disabled={isCashQuotation} />
                    </Form.Item>
                  </Col>
                </>
              )}

              <Col xs={24} sm={12}>
                <Form.Item label="Notes" name="notes">
                  <Input.TextArea rows={2} placeholder="Additional notes..." />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item label="Terms" name="terms">
                  <Input.TextArea rows={2} placeholder="Terms and conditions..." />
                </Form.Item>
              </Col>
            </Row>
          </div>
        </div>

        {/* Billable Services */}
        {showBillableServices && (
          <div className="data-table-card" style={{ marginBottom: 16 }}>
            <Card title={<Space><span>Billable Services</span><Badge count={form.getFieldValue('billableServices')?.length || 0} style={{ backgroundColor: '#3b82f6' }} /></Space>}>
              <Form.List name="billableServices">
                {(fields: FormListFieldData[], { add, remove }: FormListOperation) => (
                  <>
                    {fields.map(({ key, name, ...restField }: any, index: number) => (
                      <Card key={key} size="small" type="inner" style={{ marginBottom: 12 }} title={`Service #${index + 1}`}
                        extra={<Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => { remove(name); setTimeout(() => updateTotalsWithServices(), 0); }} />}>
                        <Row gutter={[16, 16]}>
                          <Col xs={24} sm={24} md={12}>
                            <Form.Item {...restField} label="Description" name={[name, 'description']} rules={[{ required: true, message: 'Required' }]}>
                              <Input.TextArea rows={2} placeholder="Service description..." onChange={() => updateTotalsWithServices()} />
                            </Form.Item>
                          </Col>
                          <Col xs={12} sm={12} md={6}>
                            <Form.Item {...restField} label="Tax" name={[name, 'taxId']} rules={[{ required: true, message: 'Required' }]}>
                              <Select placeholder="Tax type" loading={taxesLoading} options={taxOptions} onChange={(value) => { handleServiceTaxSelect(value, name); updateTotalsWithServices(); }} />
                            </Form.Item>
                          </Col>
                          <Col xs={12} sm={12} md={6}>
                            <Form.Item {...restField} label="Price" name={[name, 'price']} rules={[{ required: true, message: 'Required' }, { type: 'number', min: 0.01, message: 'Must be > 0' }]}>
                              <InputNumber placeholder="0.00" min={0} max={999999} precision={2} style={{ width: '100%' }} onChange={() => updateTotalsWithServices()} />
                            </Form.Item>
                          </Col>
                          <Form.Item {...restField} name={[name, 'taxRate']} hidden><InputNumber /></Form.Item>
                          <Form.Item {...restField} name={[name, 'taxType']} hidden><Input /></Form.Item>
                        </Row>
                      </Card>
                    ))}
                    <Button type="dashed" onClick={() => {
                      const defaultTaxId = taxOptions.length > 0 ? taxOptions[0].value : undefined;
                      const defaultTaxType = taxOptions.length > 0 ? taxOptions[0].data.taxType : undefined;
                      const defaultTaxRate = taxOptions.length > 0 ? taxOptions[0].data.rate : standardTaxRate;
                      add({ description: '', price: 0, taxId: defaultTaxId, taxRate: defaultTaxRate, taxType: defaultTaxType });
                      setTimeout(() => updateTotalsWithServices(), 0);
                    }} block icon={<PlusOutlined />} style={{ marginTop: 8 }}>Add Service</Button>
                  </>
                )}
              </Form.List>
            </Card>
          </div>
        )}

        {/* Quotation Items */}
        <div className="data-table-card" style={{ marginBottom: 16 }}>
          <Card title={<Space><span>Quotation Items</span><Badge count={form.getFieldValue('items')?.length || 0} style={{ backgroundColor: '#3b82f6' }} /></Space>}>
            <Form.List name="items">
              {(fields: FormListFieldData[], { add, remove }: FormListOperation) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Card key={key} size="small" type="inner" style={{ marginBottom: 12 }} extra={<Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)} />}>
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={8}>
                          <Form.Item {...restField} label="Item Code" name={[name, 'itemCode']} rules={[{ required: true, message: 'Required' }]}>
                            <Select showSearch placeholder="Search by code or name" onChange={(value) => handleItemSelect(value, name)}
                              filterOption={(input: string, option: any) => { if (!input || !option?.label) return true; return option.label.toString().toLowerCase().includes(input.toLowerCase()); }}
                              options={itemQueryResult?.data?.data?.map((item: any) => ({ label: `${item.itemCode} - ${item.itemName}`, value: item.itemCode })) || []}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Form.Item {...restField} label="Item Name" name={[name, 'itemName']} rules={[{ required: true, message: 'Required' }]}>
                            <Input readOnly placeholder="Item Name" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Form.Item {...restField} label="Price Option" name={[name, 'priceId']} rules={[{ required: true, message: 'Select price' }]}>
                            <Select placeholder="Select price option" onChange={(value) => handlePriceSelect(value, name)}
                              options={(() => {
                                const currentItem = form.getFieldValue('items')?.[name];
                                if (!currentItem?.inventoryItemId) return [];
                                const itemData = itemQueryResult?.data?.data?.find((item: any) => item.id === currentItem.inventoryItemId);
                                if (!itemData?.prices) return [];
                                return itemData.prices.filter((price: any) => price.isActive).map((price: any) => {
                                  let discountText = '';
                                  if (price.discountType === "Percentage" && price.discountValue > 0) discountText = ` (-${price.discountValue}%)`;
                                  else if (price.discountType === "FixedAmount" && price.discountValue > 0) discountText = ` (-${price.currencyCode} ${price.discountValue})`;
                                  const displayName = price.name || "Default";
                                  return { label: `${displayName}: ${price.currencyCode} ${price.effectivePrice.toFixed(2)}${discountText}`, value: price.id };
                                });
                              })()}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={6} md={6}>
                          <Form.Item {...restField} label="Quantity" name={[name, 'quantity']} rules={[{ required: true, message: 'Required' }]}>
                            <InputNumber placeholder="Qty" min={0} max={999999} onChange={() => handleQuantityChange(name)} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={6} md={6}>
                          <Form.Item {...restField} label="Unit Price" name={[name, 'unitPrice']}>
                            <InputNumber placeholder="Price" min={0} max={999999} precision={3} onChange={() => handlePriceChange(name)} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={6} md={6}>
                          <Form.Item {...restField} label="Tax" name={[name, 'taxId']} rules={[{ required: true, message: 'Required' }]}>
                            <Select placeholder="Select tax" loading={taxesLoading} options={taxOptions} onChange={(value) => handleItemTaxSelect(value, name)} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={6} md={6}>
                          <Form.Item {...restField} label="Total Amount" name={[name, 'totalAmount']}>
                            <InputNumber readOnly placeholder="Total" min={0} max={999999} precision={3} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        {/* Hidden fields */}
                        <Form.Item {...restField} name={[name, 'itemClassCode']} hidden><Input /></Form.Item>
                        <Form.Item {...restField} name={[name, 'taxRate']} hidden><InputNumber min={0} max={100} step={0.01} /></Form.Item>
                        <Form.Item {...restField} name={[name, 'taxType']} hidden><Input /></Form.Item>
                        <Form.Item {...restField} name={[name, 'discountRate']} hidden><InputNumber min={0} max={1} step={0.01} /></Form.Item>
                        <Form.Item {...restField} name={[name, 'discountAmount']} hidden><InputNumber min={0} max={999999} precision={3} /></Form.Item>
                        <Form.Item {...restField} name={[name, 'taxAmount']} hidden><InputNumber min={0} max={999999} precision={3} /></Form.Item>
                        <Form.Item {...restField} name={[name, 'lineAmount']} hidden><InputNumber min={0} max={999999} precision={3} /></Form.Item>
                      </Row>
                    </Card>
                  ))}
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={{ marginTop: 8 }}>Add Item</Button>
                </>
              )}
            </Form.List>
          </Card>
        </div>

        {/* Summary */}
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <div className="info-card">
              <div className="info-card-header"><span className="info-card-title">Currency Settings</span></div>
              <div className="info-card-body">
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12}>
                    <Form.Item label="Currency" name="currencyTypeCode" rules={[{ required: true, message: 'Required' }]}>
                      <Select placeholder="Select currency" {...currencySelectProps} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item label="Exchange Rate" name="exchangeRate" rules={[{ required: true, message: 'Required' }]}>
                      <InputNumber placeholder="Exchange rate" min={0} max={999999} precision={4} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div className="info-card">
              <div className="info-card-header"><span className="info-card-title">Quotation Summary</span></div>
              <div className="info-card-body">
                <Form.Item shouldUpdate={(prevValues, currentValues) =>
                  prevValues.totalTaxableAmount !== currentValues.totalTaxableAmount ||
                  prevValues.totalTax !== currentValues.totalTax ||
                  prevValues.totalAmount !== currentValues.totalAmount
                }>
                  {({ getFieldValue }) => (
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      <Row justify="space-between">
                        <Text type="secondary">Subtotal:</Text>
                        <Text>${(getFieldValue('totalTaxableAmount') || 0).toFixed(2)}</Text>
                      </Row>
                      <Row justify="space-between">
                        <Text type="secondary">Tax:</Text>
                        <Text>${(getFieldValue('totalTax') || 0).toFixed(2)}</Text>
                      </Row>
                      <Row justify="space-between" style={{ borderTop: '1px solid var(--surface-border)', paddingTop: 8 }}>
                        <Text strong>Total:</Text>
                        <Text strong style={{ fontSize: 18, color: 'var(--color-primary)' }}>${(getFieldValue('totalAmount') || 0).toFixed(2)}</Text>
                      </Row>
                    </Space>
                  )}
                </Form.Item>
                {/* Hidden form fields */}
                <Form.Item name="subTotal" hidden><InputNumber min={0} max={999999} precision={3} /></Form.Item>
                <Form.Item name="totalTaxableAmount" hidden><InputNumber min={0} max={999999} precision={3} /></Form.Item>
                <Form.Item name="totalTax" hidden><InputNumber min={0} max={999999} precision={3} /></Form.Item>
                <Form.Item name="totalAmount" hidden><InputNumber min={0} max={999999} precision={3} /></Form.Item>
              </div>
            </div>
          </Col>
        </Row>

        {/* Hidden form fields */}
        <Form.Item name="status" hidden><Input /></Form.Item>

        {/* Submit Button */}
        <Button {...saveButtonProps} type="primary" size="large" style={{ width: '100%', marginTop: 24 }}>Create Quotation</Button>
      </Form>
    </div>
  );
};
